import type { ApiError, OpenapiError, PaymentServiceComponents, UseQueryOverrideOptions, UseQueryReturn } from '@core/types'
import type { paths } from '@core/types/clients/payments'
import type { PaymentMethod } from '@payments/types'
import { isRefOrGetter, toValue, useCoreConfig, useNuxtApp, usePaymentsClient, usePaymentsConfig, useQuery } from '#imports'

type Ok<Path extends keyof paths> = paths[Path]['get'] extends { responses: { 200: { content: { 'application/json': infer Body } } } } ? Body : never
type Query<TResponse> = UseQueryReturn<TResponse, ApiError<TResponse> | OpenapiError<TResponse> | Error | null>
type FailedDepositReport = PaymentServiceComponents['schemas']['FailedDepositRequest']

export interface UseDepositClientReturn {
    useConfig: (options?: UseQueryOverrideOptions) => Query<Ok<'/public/cashier/deposit/configs'> | undefined>
    usePaymentMethods: (options?: UseQueryOverrideOptions) => Query<PaymentMethod[] | Ok<'/public/cashier/deposit/payment-methods'> | undefined>
    useUnprocessedWithdrawals: (options?: UseQueryOverrideOptions) => Query<Ok<'/public/transactions/withdrawals/cancel-info'> | undefined>
    reportFailedDeposit: (report: FailedDepositReport) => void
}

/**
 * The deposit page's own queries, apart from `useDeposit`, so a prefetch can start them without
 * loading the payment SDKs or reading deposit state.
 */
export function useDepositClient(): UseDepositClientReturn {
    const nuxtApp = useNuxtApp()
    const { site } = useCoreConfig()
    const { client } = usePaymentsClient()
    const { deposit } = usePaymentsConfig()

    function useConfig(options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/cashier/deposit/configs', {
                signal,
                params: {
                    query: {
                        brand: site.brand as PaymentServiceComponents['schemas']['Brand']
                    }
                },
                retry: 3
            })

            return data
        }, {
            lazy: true,
            server: false,
            ...options,
            key: 'deposit:config',
        })
    }

    function usePaymentMethods(options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/cashier/deposit/payment-methods', {
                signal,
                retry: 3,
            })

            if (!data?.options) return data

            return data.options as PaymentMethod[]
        }, {
            lazy: true,
            server: false,
            ...options,
            key: 'deposit:payment-methods',
        })
    }

    function useUnprocessedWithdrawals(options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/transactions/withdrawals/cancel-info', { signal })

            return data
        }, {
            lazy: true,
            server: false,
            ...options,
            enabled: () => {
                if (!deposit.useUnprocessedWithdrawals) return false
                if (options.enabled === false) return false

                if (isRefOrGetter(options.enabled)) {
                    return toValue(options.enabled)
                }

                return options.enabled ?? true
            },
            key: 'deposit:unprocessed-withdrawals',
        })
    }

    // A rejected report must neither send the player to the lobby nor reach APM. Reports are rare and fire
    // from SDK callbacks, so that client is built per report, inside the app context.
    function reportFailedDeposit(report: FailedDepositReport) {
        nuxtApp.runWithContext(() => {
            const { client: reportClient } = usePaymentsClient({ ignoreUnauthorized: true, reportErrors: false })

            return reportClient.POST('/public/cashier/deposit/failed', { body: report })
        }).catch(() => {})
    }

    return { useConfig, usePaymentMethods, useUnprocessedWithdrawals, reportFailedDeposit }
}
